import { OVERSEAS_MARKETS } from '@sccs/common';
import { InflateRequestMode } from '@sccs/types';
import { RequestHeaderConfiguration } from '../../types/ApiClientConfig';
import { SharedRequestConfiguration, VehicleParameterUnion } from '../../types/GeneralParameter';
import { DlsOverseasUrlsPerStage, DlsUrlsPerStage } from '../config/DlsConfig';
import { OneSearchOverseasUrlsPerStage, OneSearchUrlsPerStage } from '../config/OneSearchConfig';
import { DlsAlternative, DlsAlternativeClient } from './dls/DlsClientAlternative';
import { DlsSearch, DlsSearchClient } from './dls/DlsClientSearch';
import { DlsTeaser, DlsTeaserClient } from './dls/DlsClientTeaser';
import { OneSearchGraphQlClient, OneSearchVehicleTileDataParams } from './onesearch/graphql/OneSearchGraphQL';
import { VissTeaserClient, VissVehicleTileDataParams } from './viss/VissClientTeaser';

export type ClientVariants =
  | DlsTeaserClient
  | DlsAlternativeClient
  | DlsSearchClient
  | OneSearchGraphQlClient
  | VissTeaserClient;

export function createSpecificClient(
  configuration: SharedRequestConfiguration,
  parameter: VehicleParameterUnion,
): ClientVariants {
  const isFocMarket = configuration.isFoc;

  const configurationPath = getConfigurationPath(configuration, parameter.market);
  switch (configuration.mode) {
    case InflateRequestMode.BANNER_CAMPAIGN:
    case InflateRequestMode.SIMPLE_PRICE: {
      if (isFocMarket) {
        const client = new OneSearchGraphQlClient(configurationPath);
        client.setRequiredParameters(parameter as OneSearchVehicleTileDataParams, configuration);
        return client;
      } else {
        const client = new DlsSearchClient(configurationPath);
        client.setRequiredParameters(parameter as DlsSearch, configuration);
        return client;
      }
    }
    case InflateRequestMode.PI_PAGES_CARS:
    case InflateRequestMode.PI_PAGES_VANS:
    case InflateRequestMode.HOMEPAGE_CARS:
    case InflateRequestMode.HOMEPAGE_VANS:
    case InflateRequestMode.CAMPAIGN_TEASER_CARS:
    case InflateRequestMode.CAMPAIGN_TEASER_VANS: {
      if (isFocMarket) {
        const client = new OneSearchGraphQlClient(configurationPath);
        client.setRequiredParameters(parameter as OneSearchVehicleTileDataParams, configuration);
        return client;
      } else {
        const client = new DlsTeaserClient(configurationPath);
        client.setRequiredParameters(parameter as DlsTeaser, configuration);
        return client;
      }
    }
    case InflateRequestMode.ZERO_RESULT: {
      const client = new DlsAlternativeClient(configurationPath);
      client.setRequiredParameters(parameter as DlsAlternative, configuration);
      return client;
    }
    case InflateRequestMode.VEHICLE_MATCH_TEASER: {
      const client = new VissTeaserClient();
      client.setRequiredParameters(parameter as VissVehicleTileDataParams);
      return client;
    }

    default:
      throw new Error(
        `Unsupported mode provided in configuration: '${configuration.mode}'. Please ensure that the mode is one of the supported values: ${Object.values(InflateRequestMode).join(', ')}.`,
      );
  }
}

function getConfigurationPath(configuration: SharedRequestConfiguration, market: string): RequestHeaderConfiguration {
  let url;
  if (configuration.isFoc && configuration.mode !== InflateRequestMode.ZERO_RESULT) {
    url = checkIfOverseasMarket(market)
      ? OneSearchOverseasUrlsPerStage[configuration.stage]
      : OneSearchUrlsPerStage[configuration.stage];
  } else {
    url =
      checkIfOverseasMarket(market) && configuration.stage === 'PROD'
        ? DlsOverseasUrlsPerStage[configuration.stage]
        : DlsUrlsPerStage[configuration.stage];
  }
  return {
    basePath: url.basePath,
    apiKey: url.apiKey,
  };
}

function checkIfOverseasMarket(market: string) {
  return market && OVERSEAS_MARKETS.includes(market);
}
